import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMERGENCY_FIELDS = [
  { name: "emergency_contact", label: "Emergency Contact Name", type: "text", testId: "emergency-contact-input" },
  { name: "emergency_relationship", label: "Relationship To You", type: "text", testId: "emergency-relationship-input" },
  { name: "emergency_email", label: "Their Email Address", type: "email", testId: "emergency-email-input" },
  { name: "emergency_phone", label: "Their Cell Number", type: "tel", testId: "emergency-phone-input" },
];

const RegisterParticipant = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    preferred_name: "",
    email: "",
    password: "",
    confirm_password: "",
    phone: "",
    town: "",
    status: "",
    how_heard: "",
    experience: "",
    emergency_contact: "",
    emergency_relationship: "",
    emergency_email: "",
    emergency_phone: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleStatusChange = (value) => {
    setFormData({ ...formData, status: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const blank = Object.entries(formData).find(([, v]) => !String(v).trim());
    if (blank) {
      toast.error("All fields are required. Please complete every field.");
      return;
    }
    
    // Validate passwords match
    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);

    try {
      // Remove confirm_password before sending
      const { confirm_password, ...submitData } = formData;
      await axios.post(`${API}/participants`, submitData);
      toast.success("Application submitted! Please wait for admin approval, then you can log in.");
      setTimeout(() => navigate('/crew/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Application failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-crew-black relative grain-overlay">
      <Navigation userType="public" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-3xl">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-crew-yellow"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Return to Previous Screen
        </Button>

        <div className="bg-crew-zinc border border-zinc-800 p-8 rounded-sm relative overflow-hidden">
          {/* Technical corner decoration */}
          <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-crew-yellow"></div>
          
          <h1 className="text-4xl font-black text-crew-yellow mb-2" data-testid="form-title">
            APPLICATION TO CREW CALL PROGRAM
          </h1>
          <p className="text-zinc-400 mb-8">All fields required. Complete the manifest below.</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="first_name" className="text-zinc-300 font-bold uppercase text-xs">First Name</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <Label htmlFor="last_name" className="text-zinc-300 font-bold uppercase text-xs">Last Name</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="preferred_name" className="text-zinc-300 font-bold uppercase text-xs">Preferred Name / Nickname</Label>
              <Input
                id="preferred_name"
                name="preferred_name"
                value={formData.preferred_name}
                onChange={handleChange}
                required
                className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                data-testid="preferred-name-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email" className="text-zinc-300 font-bold uppercase text-xs">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-zinc-300 font-bold uppercase text-xs">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="password" className="text-zinc-300 font-bold uppercase text-xs">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="Minimum 6 characters"
                    className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-crew-yellow p-2"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-zinc-300 font-bold uppercase text-xs">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    minLength={6}
                    placeholder="Re-enter your password"
                    className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-500 border-t-0 border-x-0 pr-10"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-crew-yellow p-2"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="town" className="text-zinc-300 font-bold uppercase text-xs">Town of Residence</Label>
                <Input
                  id="town"
                  name="town"
                  value={formData.town}
                  onChange={handleChange}
                  required
                  className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                  data-testid="town-input"
                />
              </div>

              <div>
                <Label htmlFor="status" className="text-zinc-300 font-bold uppercase text-xs">Status</Label>
                <Select value={formData.status} onValueChange={handleStatusChange} required>
                  <SelectTrigger 
                    className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 border-t-0 border-x-0 h-auto"
                    data-testid="status-select"
                  >
                    <SelectValue placeholder="Select your status" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="Student" className="text-white hover:bg-zinc-800">Student</SelectItem>
                    <SelectItem value="Working" className="text-white hover:bg-zinc-800">Working</SelectItem>
                    <SelectItem value="Retired" className="text-white hover:bg-zinc-800">Retired</SelectItem>
                    <SelectItem value="Other" className="text-white hover:bg-zinc-800">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="how_heard" className="text-zinc-300 font-bold uppercase text-xs">How did you hear about Crew Call?</Label>
              <Input
                id="how_heard"
                name="how_heard"
                value={formData.how_heard}
                onChange={handleChange}
                required
                className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                data-testid="how-heard-input"
              />
            </div>

            <div>
              <Label htmlFor="experience" className="text-zinc-300 font-bold uppercase text-xs">What experience do you have?</Label>
              <Textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                required
                rows={4}
                className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white rounded-sm p-4 placeholder:text-zinc-600"
                data-testid="experience-input"
              />
            </div>

            <fieldset className="border border-zinc-800 rounded-sm p-5 space-y-6" data-testid="emergency-contact-section">
              <legend className="px-2 text-crew-yellow font-bold uppercase text-xs tracking-wider">
                Emergency Contact <span className="text-zinc-500 normal-case font-normal">(in the event of emergency while you are 'on crew')</span>
              </legend>
              <div className="grid md:grid-cols-2 gap-6">
                {EMERGENCY_FIELDS.map(({ name, label, type, testId }) => (
                  <div key={name}>
                    <Label htmlFor={name} className="text-zinc-300 font-bold uppercase text-xs">{label}</Label>
                    <Input
                      id={name}
                      name={name}
                      type={type}
                      value={formData[name]}
                      onChange={handleChange}
                      required
                      className="bg-black border-b-2 border-zinc-800 focus:border-crew-yellow text-white rounded-none px-0 py-4 placeholder:text-zinc-600 border-t-0 border-x-0"
                      data-testid={testId}
                    />
                  </div>
                ))}
              </div>
            </fieldset>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-crew-yellow text-black font-bold uppercase tracking-widest hover:bg-white transition-colors rounded-sm py-6 text-lg shadow-brutal"
              data-testid="submit-button"
            >
              {loading ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterParticipant;
